import { type Placement, type Platform, toMatrixPlatform } from './placement-matrix.ts';
import {
  type ConnectionAccountType,
  type MediaKind,
  PUBLISHABLE_PLACEMENTS,
  connectionSupportsPlacement,
} from './placement-resolution.ts';

export type NativeVideoCoverMode = 'provider_default' | 'auto' | 'frame' | 'upload';

export type NativeVideoCoverProviderField = 'cover_url' | 'video_cover_timestamp_ms';

export interface NativeVideoCoverCapability {
  platform: Platform;
  placement: Placement;
  modes: readonly NativeVideoCoverMode[];
  providerField: NativeVideoCoverProviderField | null;
  shareToFeed: boolean;
  profileSurface: 'fixed_preview' | null;
  adjustmentMode: 'preview_only' | 'render_cover_asset' | null;
  verifiedAt: string;
  sourceUrl: string | null;
}

export type NativeVideoCoverReadinessCode =
  'cover_mode_unsupported' | 'cover_not_ready' | 'cover_stale' | 'profile_surface_missing';

export interface NativeVideoCoverReadiness {
  canPublish: boolean;
  code: NativeVideoCoverReadinessCode | null;
  message: string | null;
}

const UNSUPPORTED_MODES = ['provider_default'] as const;

const CAPABILITIES: Partial<
  Record<
    Platform,
    Partial<Record<Placement, Omit<NativeVideoCoverCapability, 'platform' | 'placement'>>>
  >
> = {
  instagram: {
    reel: {
      modes: ['provider_default', 'auto', 'frame', 'upload'],
      providerField: 'cover_url',
      shareToFeed: true,
      profileSurface: 'fixed_preview',
      adjustmentMode: 'render_cover_asset',
      verifiedAt: '2026-07-21',
      sourceUrl:
        'https://developers.facebook.com/documentation/instagram-platform/instagram-graph-api/reference/ig-user/media',
    },
  },
  tiktok: {
    feed: {
      modes: ['provider_default', 'auto', 'frame'],
      providerField: 'video_cover_timestamp_ms',
      shareToFeed: false,
      profileSurface: null,
      adjustmentMode: null,
      verifiedAt: '2026-07-21',
      sourceUrl: 'https://developers.tiktok.com/doc/content-posting-api-reference-direct-post',
    },
  },
};

/**
 * One provider-truth lookup shared by browser readiness and server dispatch.
 * Unsupported targets keep the provider-default mode so callers can preserve
 * a zero-click path without rendering a dead editor.
 */
export function resolveNativeVideoCoverCapability(input: {
  platform: string;
  placement: Placement;
  mediaKind: MediaKind;
  accountType?: ConnectionAccountType;
}): NativeVideoCoverCapability | null {
  const platform = toMatrixPlatform(input.platform);
  if (!platform || input.mediaKind !== 'video') return null;
  if (!PUBLISHABLE_PLACEMENTS[platform].includes(input.placement)) return null;
  if (!connectionSupportsPlacement(platform, input.placement, input.accountType)) return null;

  const capability = CAPABILITIES[platform]?.[input.placement];
  if (capability) {
    return { platform, placement: input.placement, ...capability };
  }

  return {
    platform,
    placement: input.placement,
    modes: UNSUPPORTED_MODES,
    providerField: null,
    shareToFeed: false,
    profileSurface: null,
    adjustmentMode: null,
    verifiedAt: '2026-07-21',
    sourceUrl: null,
  };
}

export function supportsNativeVideoCoverMode(
  capability: NativeVideoCoverCapability | null,
  mode: NativeVideoCoverMode,
): boolean {
  return capability?.modes.includes(mode) ?? false;
}

/**
 * Target-cover readiness is deliberately narrow and fail-closed for explicit
 * selections. Provider-default remains the zero-click path; once a creator
 * chooses a frame/upload, its immutable asset and source revision must resolve.
 */
export function evaluateNativeVideoCoverReadiness(input: {
  capability: NativeVideoCoverCapability | null;
  mode: NativeVideoCoverMode;
  coverAssetStatus?: 'preparing' | 'ready' | 'failed' | 'stale' | null;
  sourceRevisionId?: string | null;
  targetRevisionId?: string | null;
  frameTimeMs?: number | null;
  shareToFeed?: boolean;
  hasProfileSurface?: boolean;
}): NativeVideoCoverReadiness {
  if (!input.capability) {
    return { canPublish: true, code: null, message: null };
  }
  // Sharing a Reel to the profile is a separate publishing decision from the
  // cover source. Even provider-default covers need an explicit review marker
  // so Feed planning and provider dispatch cannot disagree about the grid.
  if (
    input.capability.platform === 'instagram' &&
    input.capability.placement === 'reel' &&
    input.shareToFeed === true &&
    !input.hasProfileSurface
  ) {
    return {
      canPublish: false,
      code: 'profile_surface_missing',
      message: 'Review how this cover appears on the Instagram profile grid.',
    };
  }
  if (input.mode === 'provider_default') {
    return { canPublish: true, code: null, message: null };
  }
  if (!supportsNativeVideoCoverMode(input.capability, input.mode)) {
    return {
      canPublish: false,
      code: 'cover_mode_unsupported',
      message: 'This destination does not support the selected cover type.',
    };
  }
  if (
    input.sourceRevisionId &&
    input.targetRevisionId &&
    input.sourceRevisionId !== input.targetRevisionId
  ) {
    return {
      canPublish: false,
      code: 'cover_stale',
      message: 'The video changed after this cover was selected.',
    };
  }
  if (input.coverAssetStatus === 'stale') {
    return {
      canPublish: false,
      code: 'cover_stale',
      message: 'The video changed after this cover was selected.',
    };
  }
  if (input.coverAssetStatus !== 'ready') {
    return {
      canPublish: false,
      code: 'cover_not_ready',
      message: 'The selected cover is not ready.',
    };
  }
  if (
    input.mode === 'frame' &&
    (typeof input.frameTimeMs !== 'number' ||
      !Number.isFinite(input.frameTimeMs) ||
      input.frameTimeMs < 0)
  ) {
    return {
      canPublish: false,
      code: 'cover_not_ready',
      message: 'Choose a valid frame from this video.',
    };
  }
  return { canPublish: true, code: null, message: null };
}
